import * as React from 'react';
import styled from 'styled-components';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { UserType } from '../api/users';
import { ContactTypesType } from '../api/contactType';
import { IconButton, Tooltip } from '../atoms';
import {
  InputBaseContainer,
  InputSelect,
} from '../molecules';
import { CLIENT_TYPES_VALUES } from '../constants/forms';

const ContactsTable = ({
  contacts,
  setContacts,
  contactTypes,
  error,
}: Props) => {
  const handleDeleteContact = id => {
    const filteredContacts = contacts.filter(
      contact => contact.id !== id,
    );
    setContacts(filteredContacts);
  };

  const handleContactType = (type, id) => {
    const currentContact = contacts.filter(
      contact => contact.id === id,
    );
    if (currentContact) {
      currentContact[0].contactType = type;
      const contactsAll = [...contacts, ...currentContact];
      const uniqueContacts = contactsAll.reduce(
        (x, y) =>
          x.findIndex(e => e.id === y.id) < 0
            ? [...x, y]
            : x,
        [],
      );
      setContacts(uniqueContacts);
    }
  };
  return (
    <Wrapper>
      <Header error={error}>
        <HeaderCell flex={2}>Contact Name</HeaderCell>
        <HeaderCell flex={2}>Contact Type</HeaderCell>
        <HeaderCell flex={0}>Actions</HeaderCell>
      </Header>

      {contacts?.map((contact, ndx) => {
        if (!contact.name) {
          const displayName = [
            [contact?.firstName, contact?.lastName].filter(Boolean).join(' '),
            contact?.companyName || contact?.parentCompany,
          ].filter(Boolean).join(' - ');

          const currentType = contactTypes?.find(
            ct => ct.id === contact.contactType,
          );

          const contactClientType = (contact as any)?.clientType ?? (contact as any)?.client_type;
          const isCompany = String(contactClientType) === String(CLIENT_TYPES_VALUES.COMPANY);

          return (
            <Row key={`${contact.id}${contact.firstName}`} $zebra={ndx % 2 === 1}>
              <NameCell>{displayName}</NameCell>
              <TypeCell>
                {!isCompany && (
                  <StyledSelect
                    placeholder="Select type..."
                    options={contactTypes}
                    value={currentType || null}
                    onChange={selected =>
                      handleContactType(
                        selected.id,
                        contact.id,
                      )
                    }
                    getOptionValue={(item: any) => item.id}
                    getOptionLabel={(item: any) => item.name}
                  />
                )}
              </TypeCell>
              <ActionCell>
                <DeleteButton
                  onClick={() =>
                    handleDeleteContact(contact.id)
                  }
                >
                  <Tooltip title="Delete">
                    <DeleteIcon />
                  </Tooltip>
                </DeleteButton>
              </ActionCell>
            </Row>
          );
        }
        return null;
      })}

      {(!contacts || contacts.length === 0) && (
        <EmptyRow>No contacts selected</EmptyRow>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-md);
`;

const Header = styled.div<HeaderProps>`
  display: flex;
  align-items: center;
  padding: var(--space-2) var(--space-3);
  background: var(--table-row-header);
  border-bottom: 1px solid ${({ error }) =>
    error ? 'var(--danger-500)' : 'var(--neutral-200)'};
  gap: var(--space-3);
`;

const HeaderCell = styled.div<{ flex: number }>`
  flex: ${({ flex }) => flex};
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--neutral-500);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  ${({ flex }) => flex === 0 && 'width: 48px; text-align: center;'}
`;

const Row = styled.div<{ $zebra?: boolean }>`
  display: flex;
  align-items: center;
  padding: var(--space-3);
  gap: var(--space-3);
  background: ${({ $zebra }) => ($zebra ? 'var(--table-row-alt)' : 'var(--table-row-base)')};
  border-bottom: 1px solid var(--neutral-100);
  transition: background-color var(--motion-fast) var(--ease-standard);

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: var(--table-row-hover);
  }
`;

const NameCell = styled.div`
  flex: 2;
  font-size: var(--fs-base);
  color: var(--neutral-800);
  font-weight: var(--fw-medium);
`;

const TypeCell = styled.div`
  flex: 2;
`;

const StyledSelect = styled(InputSelect)`
  .react-select__control {
    min-height: 36px;
  }
`;

const ActionCell = styled.div`
  width: 48px;
  display: flex;
  justify-content: center;
`;

const DeleteButton = styled(IconButton)`
  padding: 6px;
  color: var(--neutral-400);
  transition: color var(--motion-fast) var(--ease-standard);

  &:hover {
    color: var(--danger-500);
    background: var(--danger-50);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmptyRow = styled.div`
  padding: var(--space-4) var(--space-3);
  text-align: center;
  color: var(--neutral-400);
  font-size: var(--fs-sm);
`;

interface Props {
  contacts: Array<UserType>;
  contactTypes: Array<ContactTypesType>;
  setContacts: (data: UserType[]) => void;
  error?: string;
}

interface HeaderProps {
  error?: string;
}

export default ContactsTable;
